// Centralized canonical origin resolution based on the current hostname.
// Each brand domain keeps its own self-canonical version so orthocare.pt,
// orthocarerent.pt and orthocarerent.com all rank independently with the
// same SEO structure and content.
//
// Usage: import { siteBaseUrl } from '@/lib/siteDomain';
// Returns the canonical origin (no trailing slash), e.g. 'https://orthocare.pt'.

const DOMAIN_MAP = {
  'orthocare.pt': 'https://orthocare.pt',
  'www.orthocare.pt': 'https://orthocare.pt',
  'orthocarerent.pt': 'https://orthocarerent.pt',
  'www.orthocarerent.pt': 'https://orthocarerent.pt',
  'orthocarerent.com': 'https://orthocarerent.com',
  'www.orthocarerent.com': 'https://orthocarerent.com',
  'gestosageis.pt': 'https://gestosageis.pt',
  'www.gestosageis.pt': 'https://gestosageis.pt',
  'gestosageis.com': 'https://gestosageis.com',
  'www.gestosageis.com': 'https://gestosageis.com',
  'gestosageis.store': 'https://gestosageis.store',
  'www.gestosageis.store': 'https://gestosageis.store',
};

const DEFAULT_ORIGIN = 'https://orthocarerent.com';

export function siteBaseUrl() {
  if (typeof window === 'undefined') return DEFAULT_ORIGIN;
  const host = (window.location.hostname || '').toLowerCase();
  return DOMAIN_MAP[host] || (window.location.origin || DEFAULT_ORIGIN);
}

// Brand detection — Gestos Ágeis runs on its own domains, everything else
// (orthocare.pt / orthocarerent.*) is the Orthocare brand.
export function isGestosAgeisDomain() {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  return host === 'gestosageis.pt' || host === 'www.gestosageis.pt' ||
         host === 'gestosageis.com' || host === 'www.gestosageis.com' ||
         host === 'gestosageis.store' || host === 'www.gestosageis.store' ||
         host === 'gestos.base44.app' ||
         host.endsWith('.gestosageis.store');
}

// Rental-only brand: Orthocare (orthocare.pt / orthocarerent.pt / .com) rents
// only. Gestos Ágeis (gestosageis.pt / .com / .store) sells AND rents, so its
// sale pages stay indexable. Used to keep the two brands' search results apart:
// sale pages must never be indexed on a rental domain.
const ORTHOCARE_HOSTS = [
  'orthocare.pt', 'www.orthocare.pt', 'orthocare.com', 'www.orthocare.com',
  'orthocarerent.pt', 'www.orthocarerent.pt',
  'orthocarerent.com', 'www.orthocarerent.com',
];

export function isOrthocareDomain() {
  if (typeof window === 'undefined') return false;
  return ORTHOCARE_HOSTS.includes((window.location.hostname || '').toLowerCase());
}

// Sale-intent routes: online shop (/Shop*), /loja/*, /loja-ortopedia-*,
// /comprar-*, /compra/*, "melhor loja" pages. Path-based so it also covers the
// shop pages that render outside the Layout wrapper.
const SALE_PATH_PREFIXES = ['/shop', '/loja/', '/loja-', '/compra', '/melhor-loja-'];

export function isSalePath(pathname) {
  const p = (pathname || '').toLowerCase();
  return SALE_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix));
}

// Strip the canonical origin from an absolute URL to get a route path.
// Useful for internal <Link to=...> from absolute canonical URLs.
export function toSitePath(absoluteUrl) {
  if (!absoluteUrl) return '';
  const base = siteBaseUrl();
  if (absoluteUrl.startsWith(base)) return absoluteUrl.slice(base.length) || '/';
  try {
    const u = new URL(absoluteUrl);
    return u.pathname + u.search;
  } catch (e) {
    return absoluteUrl;
  }
}

// Per-domain brand identity so Google and AI assistants treat each registered
// domain as a DISTINCT entity (different name, sameAs, url) rather than
// consolidating them. orthocare.* → brand "Orthocare Rent"; gestosageis.* →
// brand "Gestos Ágeis". Both belong to the same legal entity (Gestos Ágeis
// Unipessoal Lda, NIF 515948039) but trade as separate brands on separate
// domains, so each domain gets its own Organization node + sameAs set.
const SHARED_CORP_LOGO =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/b26bcd5b7_file_00000000133c71f492b27b24b7b10621.png';

export function brandIdentity() {
  const base = siteBaseUrl();
  if (isGestosAgeisDomain()) {
    return {
      brandName: 'Gestos Ágeis',
      orgName: 'Gestos Ágeis',
      legalName: 'Gestos Ágeis Unipessoal Lda',
      alternateName: ['Gestos Ágeis, Lda', 'Orthocare Rent', 'Orthocare'],
      bizName: 'Gestos Ágeis',
      url: base,
      logo: SHARED_CORP_LOGO,
      sameAs: [
        base,
        'https://orthocare.pt',
        'https://orthocarerent.com',
        'https://www.facebook.com/gestos.ageis',
        'https://www.instagram.com/gestos.ageis',
        'https://www.google.com/maps/place/Gestos+%C3%81geis/@38.7436,-9.1952,17z'
      ]
    };
  }
  return {
    brandName: 'Orthocare Rent',
    orgName: 'Orthocare Rent',
    legalName: 'Gestos Ágeis Unipessoal Lda',
    alternateName: ['Gestos Ágeis, Lda', 'Gestos Ágeis', 'Orthocare'],
    bizName: 'Orthocare Rent – Gestos Ágeis',
    url: base,
    logo: SHARED_CORP_LOGO,
    sameAs: [
      base,
      'https://gestosageis.pt',
      'https://gestosageis.com',
      'https://www.facebook.com/orthocare.rent',
      'https://www.instagram.com/orthocare_rent',
      'https://www.linkedin.com/company/orthocare-rent',
      'https://www.google.com/maps/place/Gestos+%C3%81geis/@38.7436,-9.1952,17z'
    ]
  };
}